import { createContext, useContext } from 'react'

/**
 * Where a grid puts its titlebar actions (search, Export, Share, add) when a
 * header above it stands in for its titlebar: a page's header (the layouts
 * provide the scope) or a card's (`DashboardCard`). That header already names
 * the grid, so the grid draws no titlebar of its own and its actions go to
 * the end of the header's row, where a data table's titlebar has them. The
 * header marks the spot with `TitlebarSlotTarget`; `TableTitlebar` moves its
 * actions there by itself, so no host decides where they go.
 */
export interface TitlebarSlot {
  /** The element the actions are drawn into, while such a header is mounted. */
  element: HTMLElement | null
  /** The header's callback ref for the spot. */
  setElement: (element: HTMLElement | null) => void
}

export const TitlebarSlotContext = createContext<TitlebarSlot | null>(null)

/** The nearest header's spot for a grid's titlebar actions, or null when no header stands in. */
export function useTitlebarSlot(): HTMLElement | null {
  return useContext(TitlebarSlotContext)?.element ?? null
}

/** The header's callback ref for that spot (a no-op outside a scope). */
export function useTitlebarSlotRef(): (element: HTMLElement | null) => void {
  return useContext(TitlebarSlotContext)?.setElement ?? noop
}

function noop() {}
